import { cssBundleHref } from '@remix-run/css-bundle';
import {
  json,
  type DataFunctionArgs,
  type HeadersFunction,
  type LinksFunction,
  type V2_MetaFunction } from
'@remix-run/node';
import {
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  isRouteErrorResponse,
  useRouteError,
  useLocation } from
'@remix-run/react';
import { withSentry } from '@sentry/remix';
import fontStylestylesheetUrl from './styles/font.css';
import tailwindStylesheetUrl from './styles/tailwind.css';
import { ClientHintCheck, getHints } from './utils/client-hints.tsx';
import { getEnv } from './utils/env.server.ts';
import { combineHeaders, getDomainUrl } from './utils/misc.ts';
import { useNonce } from './utils/nonce-provider.ts';
import { makeTimings } from './utils/timing.server.ts';
import { href as iconsHref } from './components/ui/icon.tsx';
import { Confetti } from './components/confetti.tsx';
import { getFlashSession } from './utils/flash-session.server.ts';
import { useToast } from './utils/useToast.tsx';
import { Toaster } from './components/ui/toaster.tsx';
import rdtStylesheetUrl from 'remix-development-tools/stylesheet.css';
import { lazy, Suspense, useEffect } from 'react';
import Header from './components/header.tsx';
import Footer from './components/footer.tsx';
import { useScrollIntoView } from '@mantine/hooks';
// @ts-ignore
const RemixDevTools =
process.env.NODE_ENV === 'development' ?
lazy(() => import('remix-development-tools')) :
undefined;

export const links: LinksFunction = () => {
  return [
  // Preload svg sprite as a resource to avoid render blocking
  { rel: 'preload', href: iconsHref, as: 'image' },
  // Preload CSS as a resource to avoid render blocking
  { rel: 'preload', href: fontStylestylesheetUrl, as: 'style' },
  { rel: 'preload', href: tailwindStylesheetUrl, as: 'style' },
  cssBundleHref ? { rel: 'preload', href: cssBundleHref, as: 'style' } : null,
  rdtStylesheetUrl && process.env.NODE_ENV === 'development' ?
  { rel: 'preload', href: rdtStylesheetUrl, as: 'style' } :
  null,
  { rel: 'mask-icon', href: '/favicons/mask-icon.svg' },
  {
    rel: 'alternate icon',
    type: 'image/png',
    href: '/favicons/favicon-32x32.png'
  },
  { rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' }, (
  {
    rel: 'manifest',
    href: '/site.webmanifest',
    crossOrigin: 'use-credentials'
  } as const), // necessary to make typescript happy
  //These should match the css preloads above to avoid css as render blocking resource
  { rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
  { rel: 'stylesheet', href: fontStylestylesheetUrl },
  { rel: 'stylesheet', href: tailwindStylesheetUrl },
  rdtStylesheetUrl && process.env.NODE_ENV === 'development' ?
  { rel: 'stylesheet', href: rdtStylesheetUrl } :
  null,
  cssBundleHref ? { rel: 'stylesheet', href: cssBundleHref } : null].
  filter(Boolean);
};

export const meta: V2_MetaFunction = () => {
  return [
  { title: 'Via :: Work Study Pray' },
  { name: 'description', content: 'Work Study Pray' }];

};

export async function loader({ request }: DataFunctionArgs) {
  const timings = makeTimings('root loader');

  const { flash, headers: flashHeaders } = await getFlashSession(request);

  return json(
    {
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname
      },
      ENV: getEnv(),
      flash
    },
    {
      headers: combineHeaders(
        { 'Server-Timing': timings.toString() },
        flashHeaders
      )
    }
  );
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const headers = {
    'Server-Timing': loaderHeaders.get('Server-Timing') ?? ''
  };
  return headers;
};

export type RootOutletContext = {
  missionScroll: ReturnType<typeof useScrollIntoView<HTMLDivElement>>;
  storyScroll: ReturnType<typeof useScrollIntoView<HTMLDivElement>>;
};

function App() {
  const location = useLocation();
  const data = useLoaderData<typeof loader>();
  const nonce = useNonce();
  useToast(data.flash?.toast);

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  const missionScroll = useScrollIntoView<HTMLDivElement>({
    offset: 0
  });
  const storyScroll = useScrollIntoView<HTMLDivElement>({
    offset: 0
  });

  const scrollContext = {
    missionScroll: missionScroll,
    storyScroll: storyScroll
  };

  return (
    <html lang="en" className={`light h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				<Links />
			</head>
			<body className="bg-background font-serif text-foreground">
				<Header scrollContext={scrollContext} />
				<Outlet context={scrollContext} />
				<Footer />
				<Confetti confetti={data.flash?.confetti} />
				<Toaster />
				<ScrollRestoration />
				<Scripts nonce={nonce} />
				<script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(data.ENV)}`
          }} />

				<LiveReload nonce={nonce} />
				{RemixDevTools &&
        <Suspense>
						<RemixDevTools showRouteBoundaries />
					</Suspense>}

			</body>
		</html>);

}

export function ErrorBoundary() {
  const error = useRouteError();
  if (isRouteErrorResponse(error)) {
    return <div />;
  }
  return <div />;
}

export default withSentry(App);